/**
 * LogoutHandler Component
 *
 * Handles user sign-out and redirects to the login page.
 * This component is mounted at /logout and performs the following:
 * 1. Signs out the user from Firebase
 * 2. Shows a loading/confirmation state
 * 3. Redirects to the login page
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../auth';
import { Loading } from '../components';

/**
 * Logout status for UI state management
 */
type LogoutStatus = 'processing' | 'complete' | 'error';

/**
 * LogoutHandler Component
 *
 * Automatically signs out the user and redirects to login.
 */
export function LogoutHandler() {
  const { logout, isAuthenticated } = useAuth();
  const [status, setStatus] = useState<LogoutStatus>('processing');
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    const performLogout = async () => {
      try {
        if (isAuthenticated) {
          await logout();
        }

        if (mounted) {
          setStatus('complete');

          // Redirect to login page after a brief delay
          setTimeout(() => {
            window.location.replace('/');
          }, 1500);
        }
      } catch (err) {
        console.error('[LogoutHandler] Logout failed:', err);
        if (mounted) {
          setStatus('error');
          setError(err instanceof Error ? err : new Error('Logout failed'));
        }
      }
    };

    performLogout();

    return () => {
      mounted = false;
    };
  }, [isAuthenticated, logout]);

  // Render based on status
  switch (status) {
    case 'processing':
      return <Loading fullScreen message="Signing you out..." />;

    case 'complete':
      return (
        <div className="logout-container">
          <div className="logout-content">
            <div className="logout-icon" aria-hidden="true">
              ✓
            </div>
            <h2>Signed Out</h2>
            <p>You have been successfully signed out.</p>
            <p className="redirect-message">Redirecting to login...</p>
            <a href="/" className="logout-link">
              Return to login
            </a>
          </div>
        </div>
      );

    case 'error':
      return (
        <div className="logout-error">
          <h2>Sign Out Failed</h2>
          <p>{error?.message ?? 'An unexpected error occurred'}</p>
          <div className="logout-actions">
            <button
              onClick={() => {
                setStatus('processing');
                setError(null);
              }}
              className="retry-button"
            >
              Try Again
            </button>
            <a href="/" className="logout-link">
              Return to login
            </a>
          </div>
        </div>
      );

    default:
      return <Loading fullScreen />;
  }
}
